import { TagToken } from 'tag/ts/smart_tag/model/enums/TagToken';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { IParamsVO } from 'tag/ts/tag/interfaces/params/IParamsVO';
import { FreewheelModel } from 'tag/ts/tag/model/ads/freewheel/FreewheelModel';
import { TagConstants } from 'tag/ts/tag/model/TagConstants';
import { AdSDK } from 'tag/ts/tag/model/type/ads/AdSDK';
import { Manifest } from 'tag/ts/tag/model/type/Manifest';
import { AbstractAssetPlaybackUtility } from 'tag/ts/tag/player/adapters/avia/utilities/AbstractAssetPlaybackUtility';
import { FWUtilities } from 'tag/ts/tag/utilities/FWUtilities';

/**
 * @copyright © Paramount Streaming
 * @author  Zeeshan Ahmad
 * @description Provides functionality for asset playback via asset url
 */
export class AssetUrlPlaybackUtility extends AbstractAssetPlaybackUtility {

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				name: 'AssetUrlPlaybackUtility',
				...argument
			}
		);
	}

	/**
	 * @description Returns media url required to play current asset
	 * @return {string}
	 */
	getMediaUrl(): string {

		// USE CASE 1:
		if ( this.isDAIAsset() ) {
			// DAI asset URL
			// Process DAI SSB use case => append iu param to stream URL
			// gives avia player a video manifest URL for a playback, add FW params as query params
			return this.#getMediaUrlForDaiAsset();
		}

		// USE CASE 2:
		// regular asset URL i.e. local live station: Syncbak
		// gives avia player a video manifest URL for a playback
		return this.#getMediaUrlForNonDaiAsset();
	}

	/**
	 * @description Returns params.CONTENT.assetURL with Freewheel parameters and iu appended, if required. Used for assets which use DAI.
	 * @return {string}
	 */
	#getMediaUrlForDaiAsset = (): string => {
		const adSDKType = this.tag?.model?.adUtilities?.adSetupInfo()?.sdk?.type;
		const isIntl = this.tag.tagUtilities.isInternationalContext( this?.tag );
		const fwUtil = FWUtilities.getInstance();
		const fwModel = new FreewheelModel( { tag: this.tag } );
		let mediaUrl: string;

		// do not append iu if Ad SDK setup is required
		// do not append iu if INTL - INFO: https://jira.cbsi.com/browse/CBSVIDEO-4957
		if (
			adSDKType === AdSDK.NONE &&
			!isIntl
		) {
			mediaUrl = this.#appendIuToDaiSsbUrl( fwUtil.createFWUrl( fwModel, this?.params?.CONTENT?.assetURL ) );
			this.logger.log( 'getMediaUrlForDaiAsset', '=> params.CONTENT.assetURL with Freewheel parameters and iu appended:', mediaUrl );
			return mediaUrl;
		}

		mediaUrl = fwUtil.createFWUrl( fwModel, this?.params?.CONTENT?.assetURL );
		this.logger.log( 'getMediaUrlForDaiAsset', '=> params.CONTENT.assetURL with Freewheel parameters appended:', this?.params?.CONTENT?.assetURL );
		return mediaUrl;
	};

	/**
	 * @description Appends iu parameter to DAI SSB stream URL. Attempts to validate url is DAI SSB stream before modifying url. If url is not DAI SSB, returns url unmodified.
	 * @param {string} url
	 * @return {string}
	 */
	#appendIuToDaiSsbUrl = ( url: string ): string => {
		// get iuValue from Smart Tag params first
		const iuValue = this?.params?.AD?.IMA?.iu || this.#getIUForPartnerID( this.params );

		// append iu parameter if MPX metadata indicates DAI Live stream
		if ( this.isDaiLiveStream() ) {
			return this?.utils?.URL?.replaceParam( 'iu', iuValue, url );
		}

		// append iu parameter if asset url indicates asset is DAI and using HLS or DASH
		let manifest = this.tag.model.contentUtilities.assetManifestExtension( url );
		if ( this.utils.Type.isString( manifest ) ) {
			manifest = manifest.toLowerCase();
		}

		if (
			this.tag.model.contentUtilities.daiAssetURL( url ) && // DAI URL
			( manifest === Manifest.M3U8 || manifest === Manifest.MPD ) // HLS or DASH
		) {
			return this?.utils?.URL?.replaceParam( 'iu', iuValue, url );
		}

		// not DAI SSB, return url unmodified
		this.logger.log( 'appendIuToDaiSsbUrl', 'could not identify url as DAI SSB stream url... returning url unmodified:', url );
		return url;
	};

	/**
	 * @description Returns params.CONTENT.assetURL with no iu parameter or Freewheel parameters appended. Used for assets which do not use DAI.
	 * @return {string}
	 */
	#getMediaUrlForNonDaiAsset = (): string => {
		this.logger.log( 'getMediaUrlForNonDaiAsset', '=> params.CONTENT.assetURL: ', this?.params?.CONTENT?.assetURL );
		return this?.params?.CONTENT?.assetURL;
	};

	/**
	 * @description Returns iu value based on partner value passed from the page
	 * @param {IParamsVO} params
	 * @return {string}
	 */
	#getIUForPartnerID = ( params: IParamsVO = this.params ): string => {
		const partnerID = this.tag.model.contentUtilities.getPartnerID( params );
		return window.SmartTag.capabilities.isDesktop
			? TagConstants.getInstance().DAI_IU.desktop.replace(
				TagToken.PARTNER_ID,
				partnerID
			)
			: TagConstants.getInstance().DAI_IU.mobile.replace(
				TagToken.PARTNER_ID,
				partnerID
			);
	};

	/**
	 * @description Checks if params.AD.FW is a valid object
	 * @return {boolean}
	 */
	#hasFWParams = (): boolean => {
		return (
			this.utils.Type.isObject( this.params?.AD?.FW )	// FW is object
			&&
			(
				this.#hasFMSParams()	// FW contains valid FMS object
				||
				this.#hasGDPRParams()	// FW contains valid GDPR object
			)
		);
	};

	/**
	 * @description Checks if params.AD.FW.FMS is a non-empty object
	 * @return {boolean}
	 */
	#hasFMSParams = (): boolean => {
		return (
			this.utils.Type.isObject( this.params?.AD?.FW?.FMS )	// FMS is an object
			&&
			!this.utils.Type.isEmpty( this.params?.AD?.FW?.FMS )	// FMS has at least 1 property
		);
	};

	/**
	 * @description Checks if params.AD.FW.GDPR is a non-empty object
	 * @return {boolean}
	 */
	#hasGDPRParams = (): boolean => {
		return (
			this.utils.Type.isObject( this.params?.AD?.FW?.GDPR )	// GDPR is an object
			&&
			!this.utils.Type.isEmpty( this.params?.AD?.FW?.GDPR )	// GDPR has at least 1 property
		);
	};
}